// Data Imports / Import Management — pure computation, no Firebase. Same
// architectural precedent as src/lib/unitHealth.ts and
// src/lib/leadershipSustainability.ts: a canonical, side-effect-free
// evaluator, tested directly (see scripts/test-data-imports.ts), never
// re-derived ad hoc inside a component.
//
// SCOPE: this file computes CONFIGURATION and DISPLAY STATE only — whose
// responsibility a report is, what the effective reminder cadence is,
// whether "Imported Today" applies, whether a failed attempt should be
// shown. It never sends an email and never decides delivery — see
// DataImportAdminSettings' doc comment in ../types for why reminder
// DELIVERY is deliberately out of scope for this increment.
//
// AUTHORIZATION BOUNDARY: nothing here reads or writes tenantMemberships,
// grants, or revokes anything, and the "responsible person" this file
// resolves is an ownership/accountability label ONLY — it never gates who
// may perform an import (that remains role==='admin', enforced by
// firestore.rules' isAdmin() and the existing UI gate, unchanged by this
// feature).

import type { DataImportAdminSettings, DataImportAttempt, UnitReportType } from '../types';

/** Falls back to this when a tenant has never saved Import Management
 *  config at all. Deliberately defined here (not in ../types) — this file
 *  has zero REAL (non-type) imports, which is what lets it be imported
 *  directly by a plain-node test script with no bundler — see
 *  scripts/test-data-imports.ts and src/lib/unitHealth.ts's identical
 *  HAZARDOUS_WEATHER_DUE_SOON_DAYS precedent for the same reasoning. */
export const DEFAULT_DATA_IMPORT_REMINDER_DAYS = 7;

/** Structural — anything with a Firestore-Timestamp-shaped `toDate()`.
 *  Lets this module stay Firebase-free (no `firebase/firestore` value
 *  import) while still accepting real `Timestamp`s from callers. */
export interface TimestampLike {
  toDate(): Date;
}

/** A field written with `serverTimestamp()` reads back as `null` in a LIVE
 *  snapshot for the brief window between the client's optimistic local
 *  write and the server's real ack — every function in this file that
 *  reads a `Timestamp` field must tolerate that `null`, not just an absent
 *  object. Getting this wrong crashes the UI for a fraction of a second
 *  immediately after every real import (confirmed against the emulator:
 *  a freshly-created unitImports doc's `importedAt` is briefly null before
 *  the live listener re-fires with the resolved value) — this is the ONE
 *  place that tolerance is implemented, so every caller below stays a
 *  one-line null check instead of repeating the reasoning. */
function toDateOrNull(a: TimestampLike | null | undefined): Date | null {
  return a ? a.toDate() : null;
}

function localDateKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** "Today" is the client's local calendar day — deliberately not a
 *  rolling N-hour window. See the product spec: "'Today' is the natural
 *  workflow boundary," not an arbitrary expiration. A still-pending
 *  (null) timestamp is treated as "not resolved yet" (false), never a
 *  crash — the live listener re-fires with the real value within moments
 *  and this recomputes naturally. */
export function isSameLocalDay(a: TimestampLike | null | undefined, now: Date): boolean {
  const d = toDateOrNull(a);
  return d !== null && localDateKey(d) === localDateKey(now);
}

// ---- Per-report effective settings (inheritance) --------------------------

export interface EffectiveReportImportSettings {
  responsiblePersonUid: string | null;
  reminderDays: number;
  /** Per-report enabled AND the tenant's master toggle both true. This is
   *  the value that would actually gate sending a reminder, if/when
   *  delivery is ever enabled — see the file header. */
  reminderEnabled: boolean;
  /** The per-report enabled flag alone, ignoring the master toggle — lets
   *  the UI show "this report's own setting is ON, but reminders are
   *  globally OFF" rather than collapsing both into one boolean. */
  perReportReminderEnabled: boolean;
  masterEmailRemindersEnabled: boolean;
  overridden: {
    responsiblePerson: boolean;
    reminderDays: boolean;
    reminderEnabled: boolean;
  };
  anyOverridden: boolean;
}

/** Resolves ONE report's effective configuration from the tenant defaults
 *  + that report's own override object (each of the three fields
 *  independently inherited or overridden — see DataImportReportOverride's
 *  doc comment). `admin` is null before the tenant has ever saved Import
 *  Management config at all — every report then falls back to Compass's
 *  own hardcoded defaults (no responsible person, DEFAULT_DATA_IMPORT_
 *  REMINDER_DAYS, reminders OFF), never a crash or an undefined read. */
export function effectiveReportImportSettings(
  admin: Pick<
    DataImportAdminSettings,
    'defaultResponsiblePersonUid' | 'defaultReminderDays' | 'emailRemindersEnabled' | 'overrides'
  > | null,
  reportType: UnitReportType,
): EffectiveReportImportSettings {
  const override = admin?.overrides?.[reportType];
  const defaultResponsiblePersonUid = admin?.defaultResponsiblePersonUid ?? null;
  const defaultReminderDays = admin?.defaultReminderDays ?? DEFAULT_DATA_IMPORT_REMINDER_DAYS;
  const masterEmailRemindersEnabled = admin?.emailRemindersEnabled ?? false;

  const responsiblePersonOverridden = override?.responsiblePersonUid !== undefined;
  const reminderDaysOverridden = override?.reminderDays !== undefined;
  const reminderEnabledOverridden = override?.reminderEnabled !== undefined;

  const responsiblePersonUid = responsiblePersonOverridden
    ? (override!.responsiblePersonUid ?? null)
    : defaultResponsiblePersonUid;
  const reminderDays = reminderDaysOverridden ? override!.reminderDays! : defaultReminderDays;
  // A report with no explicit per-report enabled/disabled override is "on"
  // by default (subject to the master toggle) — there is no separate
  // tenant-level "default enabled" layer per the product spec; only a
  // global master switch and an optional per-report override of it.
  const perReportReminderEnabled = reminderEnabledOverridden ? override!.reminderEnabled! : true;

  return {
    responsiblePersonUid,
    reminderDays,
    // The council-wide "Email Reminders" master is retired; sending is governed by Administrative Notifications
    // (Settings → Automations → Notifications), enforced on the server. This is only the per-report setting.
    reminderEnabled: admin ? perReportReminderEnabled : false, // never configured => nothing is scheduled
    perReportReminderEnabled,
    masterEmailRemindersEnabled,
    overridden: {
      responsiblePerson: responsiblePersonOverridden,
      reminderDays: reminderDaysOverridden,
      reminderEnabled: reminderEnabledOverridden,
    },
    anyOverridden: responsiblePersonOverridden || reminderDaysOverridden || reminderEnabledOverridden,
  };
}

// ---- "Imported Today" + today's progress -----------------------------
// Both are derived EXCLUSIVELY from the existing unitImports collection
// (successful imports only) — never from the new admin/last-attempt doc.
// A failed attempt can never produce either of these, by construction:
// there is no code path from a failure into anything this section reads.

/** `lastSuccess` should be the most recent `unitImports` doc for one
 *  report type (or null if none exists yet). `importedAt` may itself be
 *  null for an instant right after a brand-new import (see toDateOrNull's
 *  doc comment) — treated as "not yet today" until it resolves. */
export function isImportedToday(
  lastSuccess: { importedAt: TimestampLike | null } | null,
  now: Date = new Date(),
): boolean {
  if (!lastSuccess) return false;
  return isSameLocalDay(lastSuccess.importedAt, now);
}

export interface TodaysProgress {
  done: number;
  total: number;
}

/** `latestByReportType` should map each of the tenant's applicable report
 *  types to its most recent SUCCESSFUL unitImports doc (or null/absent).
 *  `reportTypes` determines the denominator — pass the tenant's actual
 *  applicable report list (UNIT_REPORT_ORDER) rather than assuming a
 *  fixed count, per the product spec's "determine the actual applicable
 *  report count from the existing import workflow." */
export function todaysProgress(
  reportTypes: UnitReportType[],
  latestByReportType: Partial<Record<UnitReportType, { importedAt: TimestampLike | null } | null>>,
  now: Date = new Date(),
): TodaysProgress {
  let done = 0;
  for (const t of reportTypes) {
    if (isImportedToday(latestByReportType[t] ?? null, now)) done++;
  }
  return { done, total: reportTypes.length };
}

// ---- Failed-attempt display ------------------------------------------
// A failed attempt is shown ONLY when it is the most recent known event
// for that report — i.e. strictly newer than the last successful import.
// A success that happens after a recorded failure silently retires that
// failure from view (matching "the previous successful dataset remains
// authoritative" — nothing about how failures are DISPLAYED should imply
// the successful data is in question once a newer success exists).

export type LastAttemptDisplay =
  | { kind: 'none' }
  | { kind: 'failed'; attempt: DataImportAttempt }
  | { kind: 'noRecentFailure' };

export function lastAttemptDisplay(
  lastSuccess: { importedAt: TimestampLike | null } | null,
  lastAttempt: DataImportAttempt | null,
): LastAttemptDisplay {
  if (!lastAttempt || lastAttempt.status !== 'failed') {
    return lastSuccess || lastAttempt ? { kind: 'noRecentFailure' } : { kind: 'none' };
  }
  // The failure's own `at` (serverTimestamp()) can itself be a still-
  // pending null for an instant right after recordDataImportAttempt()
  // writes it — treat that as "can't confirm recency yet," not a crash;
  // the live listener re-fires moments later with the real value.
  const failedAtDate = toDateOrNull(lastAttempt.at);
  if (!failedAtDate) return { kind: 'noRecentFailure' };
  if (!lastSuccess) return { kind: 'failed', attempt: lastAttempt };
  const successAtDate = toDateOrNull(lastSuccess.importedAt);
  if (!successAtDate) return { kind: 'failed', attempt: lastAttempt };
  return failedAtDate.getTime() > successAtDate.getTime()
    ? { kind: 'failed', attempt: lastAttempt }
    : { kind: 'noRecentFailure' };
}

// ---- Staleness (informational only — never triggers a send) -----------

/** Whether a report's last successful import is older than its effective
 *  reminder interval — purely descriptive ("this report would be flagged
 *  once reminders are enabled"), never used to send anything in this
 *  increment. `lastSuccess` null means "never successfully imported,"
 *  which is always considered stale. */
export function isStale(
  lastSuccess: { importedAt: TimestampLike | null } | null,
  reminderDays: number,
  now: Date = new Date(),
): boolean {
  const d = lastSuccess ? toDateOrNull(lastSuccess.importedAt) : null;
  if (!d) return true; // no success on file, or its timestamp hasn't resolved yet — never treated as "fresh" by default
  const ageMs = now.getTime() - d.getTime();
  return ageMs > reminderDays * 24 * 60 * 60 * 1000;
}
